import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import {
  useGetQuotes,
  getGetQuotesQueryKey,
  useUpdateQuoteStatus,
  useGetQuoteSubmissions,
  getGetQuoteSubmissionsQueryKey,
  useUpdateSubmissionStatus,
  useGetPoorlyReceivedQuotes,
  getGetPoorlyReceivedQuotesQueryKey,
  useGetHarvestStats,
  getGetHarvestStatsQueryKey,
  useGetEnrichmentStatus,
  getGetEnrichmentStatusQueryKey,
  QuoteSubmissionEra,
} from "@workspace/api-client-react";
import { useAuthEnabled } from "@/lib/authQuery";
import { AdminFeedbackSection } from "@/components/AdminFeedbackSection";
import { CurateQuoteButton } from "@/components/curation/CurateQuoteButton";

const colorMap = {
  stone: "bg-stone-50 border-stone-200 text-stone-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
} as const;

function StatCard({ label, value, color }: { label: string; value: number; color: keyof typeof colorMap }) {
  return (
    <div className={`rounded-2xl border p-4 ${colorMap[color]}`}>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
      <p className="mt-0.5 text-xs font-medium opacity-75">{label}</p>
    </div>
  );
}

const ERA_OPTIONS = [
  { value: "ancient", label: "Ancient" },
  { value: "medieval", label: "Medieval" },
  { value: "enlightenment", label: "Enlightenment" },
  { value: "romantic", label: "Romantic" },
  { value: "modern", label: "Modern" },
  { value: "contemporary", label: "Contemporary" },
] as const;

const harvestSources = [
  {
    name: "Seed CSV",
    status: "Ready",
    command: "pnpm --filter @workspace/api-server run harvest:seed",
  },
  {
    name: "Wikiquote MediaWiki",
    status: "Enabled · rate limited",
    command: "pnpm --filter @workspace/api-server run harvest:wikiquote",
  },
];

export function HarvestPage() {
  const queryClient = useQueryClient();
  const authed = useAuthEnabled();

  const candidateParams = { status: "candidate" as const, pageSize: 20, page: 1 };
  const { data, isLoading } = useGetQuotes(candidateParams, {
    query: { queryKey: getGetQuotesQueryKey(candidateParams), enabled: authed },
  });
  const submissionsQuery = useGetQuoteSubmissions({
    query: { queryKey: getGetQuoteSubmissionsQueryKey(), enabled: authed },
  });

  const [submissionEras, setSubmissionEras] = useState<Record<number, QuoteSubmissionEra | "">>({});

  const { mutate: updateStatus, isPending } = useUpdateQuoteStatus({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetQuotesQueryKey({ status: "candidate" }) });
        queryClient.invalidateQueries({ queryKey: getGetPoorlyReceivedQuotesQueryKey({ minDismissals: 3, limit: 50 }) });
      },
    },
  });

  const { mutate: updateSubmissionStatus, isPending: isSubmissionPending } = useUpdateSubmissionStatus({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetQuoteSubmissionsQueryKey() });
      },
    },
  });

  const poorlyReceivedQuery = useGetPoorlyReceivedQuotes({ minDismissals: 3, limit: 50 }, {
    query: { queryKey: getGetPoorlyReceivedQuotesQueryKey({ minDismissals: 3, limit: 50 }), enabled: authed },
  });

  const statsQuery = useGetHarvestStats({
    query: { queryKey: getGetHarvestStatsQueryKey(), refetchInterval: 30_000, enabled: authed },
  });
  const pipelineQuery = useGetEnrichmentStatus({
    query: { queryKey: getGetEnrichmentStatusQueryKey(), refetchInterval: 30_000, enabled: authed },
  });

  const candidates = data?.data ?? [];
  const total = data?.total ?? 0;
  const submissions = submissionsQuery.data ?? [];
  const submissionsTotal = submissions.length;
  const poorlyReceived = poorlyReceivedQuery.data ?? [];

  function getSubmissionEra(submissionId: number, submittedEra: QuoteSubmissionEra | null): string {
    return submissionEras[submissionId] ?? submittedEra ?? "";
  }

  function setEraForSubmission(submissionId: number, era: QuoteSubmissionEra | "") {
    setSubmissionEras((prev) => ({ ...prev, [submissionId]: era }));
  }

  const stats = statsQuery.data;
  // aiPending now counts ai_confidence IS NULL (truly unprocessed).
  // total - aiPending = all quotes that have been seen by the AI (high+medium+low).
  const aiProcessed = stats ? stats.total - stats.aiPending : 0;
  const enrichedPct =
    stats && stats.total > 0
      ? Math.round((aiProcessed / stats.total) * 100)
      : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <Link
          href="/harvest"
          className="rounded-full bg-stone-900 px-4 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-stone-800 active:scale-[0.98]"
        >
          Harvest Queue
        </Link>
        <Link
          href="/harvest/governance"
          className="rounded-full bg-stone-100 px-4 py-1.5 text-sm font-medium text-stone-600 transition hover:bg-stone-200 active:scale-[0.98]"
        >
          Governance
        </Link>
        <Link
          href="/harvest/resolution"
          className="rounded-full bg-stone-100 px-4 py-1.5 text-sm font-medium text-stone-600 transition hover:bg-stone-200 active:scale-[0.98]"
          data-testid="link-entity-resolution"
        >
          Entity resolution
        </Link>
      </div>

      <section className="grid gap-6 xl:grid-cols-2" data-testid="page-harvest">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2" data-testid="harvest-stats">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-stone-900">Enrichment progress</h3>
            <p className="text-sm text-stone-500 mt-0.5">AI-verification state across the quote corpus</p>
          </div>
          {statsQuery.isLoading ? (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">Loading…</span>
          ) : stats ? (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
              {enrichedPct}% enriched
            </span>
          ) : null}
        </div>

        {statsQuery.isLoading && (
          <div className="space-y-3">
            <div className="h-4 animate-pulse rounded-full bg-stone-100" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-2xl bg-stone-100" />
              ))}
            </div>
          </div>
        )}

        {!statsQuery.isLoading && stats && (
          <div className="space-y-5">
            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs text-stone-500">
                <span>AI-processed ({aiProcessed} of {stats.total})</span>
                <span>{enrichedPct}%</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${enrichedPct}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard label="Total" value={stats.total} color="stone" />
              <StatCard label="Approved" value={stats.approved} color="emerald" />
              <StatCard label="Candidate" value={stats.candidate} color="amber" />
              <StatCard label="Rejected" value={stats.rejected} color="rose" />
              <StatCard label="AI Verified" value={stats.aiVerified} color="sky" />
              <StatCard label="Unprocessed" value={stats.aiPending} color="violet" />
            </div>

            {(stats.confidenceHigh + stats.confidenceMedium + stats.confidenceLow) > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-stone-500 uppercase tracking-wide">AI confidence breakdown</p>
                <div className="grid grid-cols-3 gap-3">
                  <StatCard label="High confidence" value={stats.confidenceHigh} color="emerald" />
                  <StatCard label="Medium confidence" value={stats.confidenceMedium} color="amber" />
                  <StatCard label="Low confidence" value={stats.confidenceLow} color="rose" />
                </div>
              </div>
            )}
          </div>
        )}

        {!statsQuery.isLoading && !stats && (
          <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
            <p className="text-stone-500">Stats unavailable.</p>
          </div>
        )}
      </div>
      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2" data-testid="pipeline-status">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-stone-900">Batch pipeline</h3>
            <p className="text-sm text-stone-500 mt-0.5">OpenAI enrichment job status from checkpoint file</p>
          </div>
          {pipelineQuery.isLoading ? (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">Loading…</span>
          ) : pipelineQuery.data?.found ? (
            <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-800">
              {pipelineQuery.data.total} chunks tracked
            </span>
          ) : (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">No checkpoint</span>
          )}
        </div>

        {pipelineQuery.isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-stone-100" />
            ))}
          </div>
        )}

        {!pipelineQuery.isLoading && !pipelineQuery.data?.found && (
          <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
            <p className="text-stone-500">No checkpoint file found.</p>
            <p className="mt-1 text-sm text-stone-400">
              Pipeline status appears here once <code className="font-mono text-xs bg-stone-100 px-1 py-0.5 rounded">enrich-quotes-openai</code> has run.
            </p>
          </div>
        )}

        {!pipelineQuery.isLoading && pipelineQuery.data?.found && (() => {
          const p = pipelineQuery.data;
          const appliedPct = p.total > 0 ? Math.round((p.applied / p.total) * 100) : 0;
          return (
            <div className="space-y-5">
              <div>
                <div className="mb-1.5 flex items-center justify-between text-xs text-stone-500">
                  <span>Applied ({p.applied} of {p.total} chunks)</span>
                  <span>{appliedPct}%</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
                  <div className="flex h-full rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${appliedPct}%` }}
                    />
                    {p.total > 0 && (
                      <div
                        className="h-full bg-sky-400 transition-all duration-500"
                        style={{ width: `${Math.round((p.inFlight / p.total) * 100)}%` }}
                      />
                    )}
                    {p.total > 0 && (
                      <div
                        className="h-full bg-rose-400 transition-all duration-500"
                        style={{ width: `${Math.round((p.failed / p.total) * 100)}%` }}
                      />
                    )}
                  </div>
                </div>
                <div className="mt-1.5 flex gap-4 text-xs text-stone-400">
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />Applied</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-sky-400" />In-flight</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-rose-400" />Failed</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-stone-200" />Pending</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <StatCard label="Total chunks" value={p.total} color="stone" />
                <StatCard label="Applied" value={p.applied} color="emerald" />
                <StatCard label="In-flight" value={p.inFlight} color="sky" />
                <StatCard label="Failed" value={p.failed} color="rose" />
                <StatCard label="Pending" value={p.pending} color="amber" />
              </div>
              {p.lastRunAt && (
                <p className="text-xs text-stone-400">
                  Last run:{" "}
                  <span className="text-stone-600 font-medium">
                    {new Date(p.lastRunAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </p>
              )}
            </div>
          );
        })()}
      </div>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="mb-5 text-xl font-semibold text-stone-900">Harvest sources</h3>
        <div className="space-y-3">
          {harvestSources.map((source, index) => (
            <div
              key={source.name}
              className="rounded-2xl border border-stone-200 bg-white p-4 transition-colors hover:border-stone-300"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-stone-900">{source.name}</p>
                  <p className="text-sm text-stone-500">{source.status}</p>
                </div>
                <button
                  className="rounded-xl bg-stone-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
                  data-testid={`btn-run-${index}`}
                  onClick={() => {
                    alert(`Run from terminal:\n\n${source.command}`);
                  }}
                >
                  Run
                </button>
              </div>
              <p className="mt-2 rounded-lg bg-stone-50 px-3 py-1.5 font-mono text-xs text-stone-500">
                {source.command}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-stone-900">Review queue</h3>
          {isLoading ? (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">
              Loading…
            </span>
          ) : (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
              {total} pending
            </span>
          )}
        </div>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-stone-100" />
            ))}
          </div>
        )}

        {!isLoading && candidates.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
            <p className="text-stone-500">No candidates in the review queue.</p>
            <p className="mt-1 text-sm text-stone-400">Run the harvest scripts to populate it.</p>
          </div>
        )}

        {!isLoading && candidates.length > 0 && (
          <div className="space-y-4">
            {candidates.map((quote) => (
              <div
                key={quote.id}
                className="rounded-2xl border border-stone-200 bg-white p-4 transition-colors hover:border-stone-300"
              >
                <p className="font-serif text-lg leading-relaxed text-stone-900">
                  &ldquo;{quote.text}&rdquo;
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-stone-500">
                    Candidate · {quote.author}
                    {quote.year != null ? ` · ${quote.year < 0 ? Math.abs(quote.year) + " BCE" : quote.year}` : ""}
                  </p>
                  <div className="flex gap-2">
                    <CurateQuoteButton quoteId={quote.id} label="Edit" />
                    <button
                      disabled={isPending}
                      className="rounded-xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                      data-testid={`btn-reject-${quote.id}`}
                      onClick={() =>
                        updateStatus({ id: quote.id, data: { status: "rejected" } })
                      }
                    >
                      Reject
                    </button>
                    <button
                      disabled={isPending}
                      className="rounded-xl bg-stone-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                      data-testid={`btn-approve-${quote.id}`}
                      onClick={() =>
                        updateStatus({ id: quote.id, data: { status: "approved" } })
                      }
                    >
                      Approve
                    </button>
                  </div>
                </div>
                {quote.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {quote.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-500"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-stone-900">User submissions</h3>
            <p className="text-sm text-stone-500 mt-0.5">Quotes submitted by library users for review</p>
          </div>
          {submissionsQuery.isLoading ? (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">
              Loading…
            </span>
          ) : (
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
              {submissionsTotal} pending
            </span>
          )}
        </div>

        {submissionsQuery.isLoading && (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-stone-100" />
            ))}
          </div>
        )}

        {!submissionsQuery.isLoading && submissions.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
            <p className="text-stone-500">No pending user submissions.</p>
            <p className="mt-1 text-sm text-stone-400">Users can submit quotes from their profile page.</p>
          </div>
        )}

        {!submissionsQuery.isLoading && submissions.length > 0 && (
          <div className="space-y-4">
            {submissions.map((submission) => {
              const era = getSubmissionEra(submission.id, submission.era);
              return (
                <div
                  key={submission.id}
                  className="rounded-2xl border border-stone-200 bg-white p-4 transition-colors hover:border-stone-300"
                >
                  <p className="font-serif text-lg leading-relaxed text-stone-900">
                    &ldquo;{submission.text}&rdquo;
                  </p>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-stone-700 font-medium">
                        — {submission.author}
                        {submission.source ? (
                          <span className="font-normal text-stone-500"> · {submission.source}</span>
                        ) : null}
                      </p>
                      <p className="text-xs text-stone-400 mt-0.5">
                        Submitted by{" "}
                        <span className="font-medium text-stone-500">
                          {submission.submitterUsername ?? "unknown"}
                        </span>
                        {" · "}
                        {new Date(submission.submittedAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {(() => {
                        // Backend adds quoteId to pending submissions; optional until OpenAPI is updated.
                        const linkedQuoteId = (submission as typeof submission & { quoteId?: number | null }).quoteId;
                        return linkedQuoteId ? <CurateQuoteButton quoteId={linkedQuoteId} label="Edit quote" /> : null;
                      })()}
                      <select
                        value={era}
                        onChange={(e) => setEraForSubmission(submission.id, e.target.value as QuoteSubmissionEra | "")}
                        className="rounded-xl border border-stone-200 px-2.5 py-2 text-sm text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-1"
                        aria-label="Era"
                        data-testid={`era-select-submission-${submission.id}`}
                      >
                        <option value="">Era…</option>
                        {ERA_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={isSubmissionPending}
                        className="rounded-xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                        data-testid={`btn-reject-submission-${submission.id}`}
                        onClick={() =>
                          updateSubmissionStatus({ id: submission.id, data: { status: "rejected" } })
                        }
                      >
                        Reject
                      </button>
                      <button
                        disabled={isSubmissionPending}
                        className="rounded-xl bg-stone-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                        data-testid={`btn-approve-submission-${submission.id}`}
                        onClick={() =>
                          updateSubmissionStatus({
                            id: submission.id,
                            data: {
                              status: "approved",
                              era: (era || null) as import("@workspace/api-client-react").UpdateSubmissionStatusBodyEra | null,
                            },
                          })
                        }
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-stone-900">Poorly received</h3>
            <p className="text-sm text-stone-500 mt-0.5">Quotes dismissed by ≥3 users, sorted by dismissal count</p>
          </div>
          {poorlyReceivedQuery.isLoading ? (
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">
              Loading…
            </span>
          ) : (
            <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-800">
              {poorlyReceived.length} quotes
            </span>
          )}
        </div>

        {poorlyReceivedQuery.isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-2xl bg-stone-100" />
            ))}
          </div>
        )}

        {!poorlyReceivedQuery.isLoading && poorlyReceived.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
            <p className="text-stone-500">No poorly-received quotes yet.</p>
            <p className="mt-1 text-sm text-stone-400">Quotes dismissed by 3+ users will appear here.</p>
          </div>
        )}

        {!poorlyReceivedQuery.isLoading && poorlyReceived.length > 0 && (
          <div className="space-y-4">
            {poorlyReceived.map((quote) => (
              <div
                key={quote.id}
                className="rounded-2xl border border-rose-100 bg-rose-50/40 p-4 transition-colors hover:border-rose-200"
              >
                <p className="font-serif text-lg leading-relaxed text-stone-900">
                  &ldquo;{quote.text}&rdquo;
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-stone-600 font-medium">— {quote.author}</p>
                    <p className="text-xs text-stone-400 mt-0.5">{quote.era}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold text-rose-700">
                      {quote.dismissalCount} dismissals
                    </span>
                    <button
                      disabled={isPending}
                      className="rounded-xl border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:active:scale-100"
                      data-testid={`btn-remove-poorly-${quote.id}`}
                      onClick={() =>
                        updateStatus({ id: quote.id, data: { status: "rejected" } })
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ReferralsSection />
      <AdminFeedbackSection />
    </section>
    </div>
  );
}

type ReferredUser = {
  userId: string;
  username: string | null;
  attributedAt: string;
  favoritesCount: number;
  emailEnabled: boolean;
  activeD5: boolean;
  activeD10: boolean;
  activeD15: boolean;
};

type ReferralCampaignRow = {
  campaign: string;
  signups: number;
  activeD5: number;
  activeD10: number;
  activeD15: number;
  avgFavorites: number;
  emailPct: number;
  referredUsers: ReferredUser[];
};

type ReferralReferrerRow = {
  referrerUserId: string;
  referrerUsername: string | null;
  referralCode: string | null;
  signups: number;
  activeD5: number;
  activeD10: number;
  activeD15: number;
  avgFavorites: number;
  emailPct: number;
  referredUsers: ReferredUser[];
};

type ReferralStats = {
  campaigns: ReferralCampaignRow[];
  referrers: ReferralReferrerRow[];
};

function CohortBadge({ active, total }: { active: number; total: number }) {
  const pct = total > 0 ? Math.round((active / total) * 100) : 0;
  const color =
    pct >= 60
      ? "bg-emerald-100 text-emerald-800"
      : pct >= 30
        ? "bg-amber-100 text-amber-800"
        : "bg-stone-100 text-stone-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>
      {active}/{total}
    </span>
  );
}

function ReferredUsersDrawer({ users }: { users: ReferredUser[] }) {
  return (
    <tr>
      <td colSpan={8} className="p-0">
        <div className="mx-2 mb-2 overflow-hidden rounded-xl border border-stone-100 bg-stone-50">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-left text-stone-400 uppercase tracking-wide">
                <th className="px-3 py-2 font-semibold">User</th>
                <th className="px-3 py-2 font-semibold">Joined</th>
                <th className="px-3 py-2 font-semibold text-center">D5</th>
                <th className="px-3 py-2 font-semibold text-center">D10</th>
                <th className="px-3 py-2 font-semibold text-center">D15</th>
                <th className="px-3 py-2 font-semibold text-right">Favs</th>
                <th className="px-3 py-2 font-semibold text-right">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.map((u) => (
                <tr key={u.userId} className="hover:bg-stone-100/60 transition-colors">
                  <td className="px-3 py-2 text-stone-700 font-medium">
                    {u.username ?? <span className="text-stone-400 italic">unnamed</span>}
                  </td>
                  <td className="px-3 py-2 text-stone-500">
                    {new Date(u.attributedAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {u.activeD5 ? (
                      <span className="text-emerald-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {u.activeD10 ? (
                      <span className="text-emerald-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {u.activeD15 ? (
                      <span className="text-emerald-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-stone-600">{u.favoritesCount}</td>
                  <td className="px-3 py-2 text-right">
                    {u.emailEnabled ? (
                      <span className="text-emerald-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

function ReferralsSection() {
  const authed = useAuthEnabled();
  const referralStatsQuery = useQuery({
    queryKey: ["/api/admin/referral-stats"],
    queryFn: () => customFetch<ReferralStats>("/api/admin/referral-stats", { method: "GET" }),
    enabled: authed,
    refetchInterval: 60_000,
  });

  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [expandedReferrers, setExpandedReferrers] = useState<Set<string>>(new Set());

  function toggleCampaign(key: string) {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleReferrer(key: string) {
    setExpandedReferrers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const stats = referralStatsQuery.data;
  const totalAttributions =
    (stats?.campaigns.reduce((s, c) => s + c.signups, 0) ?? 0) +
    (stats?.referrers.reduce((s, r) => s + r.signups, 0) ?? 0) -
    (stats?.referrers.filter((r) => stats.campaigns.some((c) => c.referredUsers.some((u) => r.referredUsers.some((ru) => ru.userId === u.userId)))).reduce((s, r) => s + r.signups, 0) ?? 0);

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm xl:col-span-2">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-stone-900">Referral program</h3>
          <p className="text-sm text-stone-500 mt-0.5">
            Cohort retention at day 5 / 10 / 15 — active if any favorite saved or daily pick assigned after joining · click a row to see individual users
          </p>
        </div>
        {referralStatsQuery.isLoading ? (
          <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">Loading…</span>
        ) : (
          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-800">
            {(stats?.campaigns.reduce((s, c) => s + c.signups, 0) ?? 0) +
              (stats?.referrers.reduce((s, r) => s + r.signups, 0) ?? 0)}{" "}
            total attributions
          </span>
        )}
      </div>

      {referralStatsQuery.isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-2xl bg-stone-100" />
          ))}
        </div>
      )}

      {!referralStatsQuery.isLoading && stats && (
        <div className="space-y-8">
          <div>
            <h4 className="mb-3 text-sm font-semibold text-stone-700 uppercase tracking-wide">
              By campaign
            </h4>
            {stats.campaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-stone-200 p-6 text-center">
                <p className="text-sm text-stone-400">No campaign attributions yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-stone-400 uppercase tracking-wide border-b border-stone-100">
                      <th className="pb-2 pr-4 font-semibold">Campaign</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Signups</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D5</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D10</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D15</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Avg favs</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Email %</th>
                      <th className="pb-2 font-semibold" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {stats.campaigns.map((row) => (
                      <>
                        <tr
                          key={row.campaign}
                          className="hover:bg-stone-50 transition-colors cursor-pointer"
                          onClick={() => toggleCampaign(row.campaign)}
                        >
                          <td className="py-2.5 pr-4 font-medium text-stone-800 font-mono text-xs">
                            {row.campaign}
                          </td>
                          <td className="py-2.5 pr-4 text-right text-stone-700 font-semibold">
                            {row.signups}
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD5} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD10} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD15} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-right text-stone-600">{row.avgFavorites}</td>
                          <td className="py-2.5 pr-4 text-right text-stone-600">{row.emailPct}%</td>
                          <td className="py-2.5 text-right text-stone-400 text-xs">
                            {expandedCampaigns.has(row.campaign) ? "▲" : "▼"}
                          </td>
                        </tr>
                        {expandedCampaigns.has(row.campaign) && (
                          <ReferredUsersDrawer key={`${row.campaign}-drawer`} users={row.referredUsers} />
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-stone-700 uppercase tracking-wide">
              By referrer
            </h4>
            {stats.referrers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-stone-200 p-6 text-center">
                <p className="text-sm text-stone-400">No user referrals yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-stone-400 uppercase tracking-wide border-b border-stone-100">
                      <th className="pb-2 pr-4 font-semibold">Referrer</th>
                      <th className="pb-2 pr-4 font-semibold">Code</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Signups</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D5</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D10</th>
                      <th className="pb-2 pr-4 font-semibold text-center">D15</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Avg favs</th>
                      <th className="pb-2 pr-4 font-semibold text-right">Email %</th>
                      <th className="pb-2 font-semibold" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {stats.referrers.map((row) => (
                      <>
                        <tr
                          key={row.referrerUserId}
                          className="hover:bg-stone-50 transition-colors cursor-pointer"
                          onClick={() => toggleReferrer(row.referrerUserId)}
                        >
                          <td className="py-2.5 pr-4 font-medium text-stone-800">
                            {row.referrerUsername ?? (
                              <span className="text-stone-400 italic">unnamed</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4 font-mono text-xs text-stone-500">
                            {row.referralCode ?? "—"}
                          </td>
                          <td className="py-2.5 pr-4 text-right text-stone-700 font-semibold">
                            {row.signups}
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD5} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD10} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-center">
                            <CohortBadge active={row.activeD15} total={row.signups} />
                          </td>
                          <td className="py-2.5 pr-4 text-right text-stone-600">{row.avgFavorites}</td>
                          <td className="py-2.5 pr-4 text-right text-stone-600">{row.emailPct}%</td>
                          <td className="py-2.5 text-right text-stone-400 text-xs">
                            {expandedReferrers.has(row.referrerUserId) ? "▲" : "▼"}
                          </td>
                        </tr>
                        {expandedReferrers.has(row.referrerUserId) && (
                          <ReferredUsersDrawer
                            key={`${row.referrerUserId}-drawer`}
                            users={row.referredUsers}
                          />
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {!referralStatsQuery.isLoading && !stats && (
        <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
          <p className="text-stone-500">Referral stats unavailable.</p>
        </div>
      )}
    </div>
  );
}
